<script lang="ts">
import type { InputBlockProps } from '~/types/input-block';

export interface RepeaterBlockProps extends InputBlockProps {
	schema: FormSchema<object>;

	/**
	 * Display template used for rendering the item values
	 */
	displayTemplate?: DisplayTemplate;

	displayBlock?: string | { id: string; props?: AnyItem; valueKey?: string };

	/**
	 * If true, show the repeater form in line with the list.
	 * If false, it will open a new drawer for the item edits. TODO
	 *
	 * @default true
	 */
	inline?: boolean;
}

interface InternalItem<T> {
	id: string;
	label: string;
	description?: string;
	data: T;
}

const __DsBlock = {
	hidden: true,
}</script>

<script setup lang="ts" generic="T extends AnyItem = AnyItem">
import type { Ref } from 'vue';
import type { FormSchema, FormSchemaAdapter } from '#layers/block-engine';
import type { AnyItem, DisplayTemplate } from '#layers/data-model';
import { FocusScope } from 'reka-ui';
import { computed, ref, shallowRef, useTemplateRef, watch } from 'vue';
import { useFormField } from '#imports';
import { isDeepEqual } from '#layers/base';
import { randomUUID } from '#layers/base/utils/uuid';
import { createCopyAdapter, defineBlock, getDefaultValuesFromFormSchema } from '#layers/block-engine';
import { ButtonAdd, ButtonEdit, ButtonRemove, focusFirst } from '#layers/ui';
import { usePrimitiveFormField } from '~/composables/form-block/use-primitive-form-field';

const props = withDefaults(defineProps<RepeaterBlockProps>(), {
	inline: true,
});

;

const externalModel = defineModel<T[]>();
const { modelValue: model, disabled } = usePrimitiveFormField(props, { model: externalModel, defaultValue: () => [] });
const { color, emitFormChange, emitFormBlur, emitFormFocus, emitFormInput } = useFormField(props);
const addButtonWrapperRef = useTemplateRef('addButtonWrapper');

const normalizedDisplayBlock = computed(() => {
	if (typeof props.displayBlock === 'string') {
		return {
			id: props.displayBlock,
		};
	}

	return props.displayBlock;
});

const { items, add, remove, activeItemId, toggleEditing, stopEditing, triggerItemUpdate } = useItems();
const itemAdapters = new WeakMap<InternalItem<T>, FormSchemaAdapter<T>>();

/**
 * Get the cached form adapter for an internal item.
 */
function getItemAdapter(item: InternalItem<T>): FormSchemaAdapter<T> {
	const existingAdapter = itemAdapters.get(item);

	if (existingAdapter) {
		return existingAdapter;
	}

	const itemData = computed<T>({
		get: () => item.data,
		set: (value) => {
			item.data = value;
		},
	});
	const adapter = createCopyAdapter(itemData, { onChange: () => triggerItemUpdate(item) });

	itemAdapters.set(item, adapter);
	return adapter;
}

function useItems() {
	// Since the drag and drop library requires unique IDs to correctly map items we need to enrich
	// our external model value with generated UUIDs.
	// Deeply reactive so an in-place `item.data` write (via the per-item edit adapter) is tracked;
	// a shallowRef would leave the adapter's `computed(() => item.data)` reading a stale value.
	// Typed via annotation because `ref` would otherwise apply `UnwrapRef` to the generic `T`.
	const items: Ref<InternalItem<T>[]> = ref([]);
	const activeItemId = shallowRef<string | null>(null);

	// sync model -> items only if the underlying data actually changes
	watch(model, () => {
		// this will generate new UUIDs for each item, but that is fine since we only compare the data
		const mappedItems = model.value.map((item) => toInternalItem(item));

		// if the number of items changes we can skip the hash compare
		if (mappedItems.length !== items.value.length) {
			items.value = mappedItems;
			return;
		}

		for (let i = 0; i < mappedItems.length; ++i) {
			if (!isDeepEqual(mappedItems[i]!.data, model.value[i]!)) {
				items.value = mappedItems;
			}
		}
	}, {
		immediate: true,
	});

	watch(() => items.value, () => {
		model.value = items.value.map((item) => item.data) as T[];
		emitChange();
	});

	return {
		items,
		add,
		remove,
		triggerItemUpdate,

		activeItemId,
		toggleEditing,
		stopEditing,
	};

	function triggerItemUpdate(item: InternalItem<T>) {
		const mutatedItems = [...items.value];
		const itemIndex = mutatedItems.findIndex((i) => i.id === item.id);

		if (itemIndex === -1) {
			return;
		}

		mutatedItems[itemIndex] = item;
		items.value = mutatedItems;
		model.value = mutatedItems.map((item) => item.data) as T[];
	}

	function add(item?: T) {
		item ??= getDefaultValuesFromFormSchema({
			schema: props.schema,
		}) as T;

		items.value = [...items.value, toInternalItem(item)];
		model.value = items.value.map((i) => i.data) as T[];

		toggleEditing(items.value.at(-1)!.id);
	}

	function toggleEditing(id: string) {
		if (activeItemId.value === id) {
			stopEditing();
		}
		else {
			activeItemId.value = id;
		}
	}

	function stopEditing() {
		activeItemId.value = null;

		focusFirst(addButtonWrapperRef);
	}

	function remove(id: string) {
		items.value = items.value.filter((item) => item.id !== id);
		model.value = items.value.map((item) => item.data) as T[];
	}

	function toInternalItem(item: T) {
		return {
			id: randomUUID(),
			data: item,
		} as InternalItem<T>;
	}
}

function emitChange() {
	emitFormChange();
	emitFormInput();
	emitFormBlur();
}
</script>

<template>
	<div
		class="@container/repeater rounded-md border"
		:class="{
			'border-accented': color !== 'error',
			'border-error': color === 'error',
		}"
	>
		<DAnimatedSheet
			dimension="height"
			class="divide-y"
		>
			<div ref="addButtonWrapper" class="flex h-13 items-center justify-between gap-x-3 px-6 @max-sm/repeater:pr-2">
				<span class="text-xs font-medium" :class="{ 'text-muted': items.length === 0 }">
					{{ $t('common.n_items', items.length) }}
				</span>
				<ButtonAdd
					class="-ml-2"
					size="sm"
					variant="ghost"
					:square="items.length > 0"
					:label="items.length === 0 ? $t('common.add_item') : undefined"
					:disabled
					@click="add()"
					@focus="emitFormFocus"
				/>
			</div>
			<DList
				v-if="items.length > 0"
				v-model:items="items"
				sortable
				class="rounded-t-none border-none"
				:disabled
				:allow-drag-only-on-handle="!!activeItemId"
				:ui="{
					item: {
						base: '@max-sm/repeater:pr-2',
						content: '-ml-1 pl-1',
					},
				}"
			>
				<template #item-trailing="{ item }">
					<div
						class="
							flex items-center gap-1 opacity-0
							group-hover/list-item:opacity-100
							group-focus-visible/list-item:opacity-100
						"
					>
						<ButtonEdit
							v-if="item.id !== activeItemId"
							size="sm"
							:disabled
							@click="toggleEditing(item.id)"
						/>
						<ButtonRemove
							with-leading-icon
							:disabled
							square
							@click="remove(item.id)"
						/>
					</div>
				</template>

				<template #item-content="{ item }">
					<div class="mr-1 flex min-w-0 flex-1 flex-col items-start justify-center @sm/repeater:mr-4">
						<FocusScope v-if="item.id === activeItemId" loop as-child>
							<DForm
								v-if="item.id === activeItemId"
								layout="side-by-side"
								size="sm"
								:state="item.data as object"
								class="relative w-full rounded-md border px-4 pt-4 pb-2"
								auto-focus-first-input
								@submit="stopEditing()"
								@keydown.esc.stop="stopEditing()"
							>
								<FormSchemaFields
									:schema
									:adapter="getItemAdapter(item)"
								/>

								<template #content-bottom>
									<div class="mt-2 flex justify-end">
										<DButton
											type="submit"
											color="neutral"
											variant="ghost"
											:label="$t('common.close')"
											size="xs"
											@click="stopEditing()"
										/>
									</div>
								</template>
							</DForm>
						</FocusScope>
						<DButton
							v-else
							variant="ghost"
							color="neutral"
							size="sm"
							class="-my-1.5"
							:aria-label="$t('common.edit')"
							@click="toggleEditing(item.id)"
						>
							<RenderDisplayTemplate v-if="displayTemplate" :display-template :item="item.data" />
							<BlockRender
								v-else-if="normalizedDisplayBlock"
								:block="normalizedDisplayBlock.id"
								:props="{
									item: item.data,
									...normalizedDisplayBlock.props,
									...(normalizedDisplayBlock.valueKey ? { value: item.data[normalizedDisplayBlock.valueKey] } : {}) }"
							/>
						</DButton>
					</div>
				</template>
			</DList>
		</DAnimatedSheet>
	</div>
</template>
